import { useCallback, useEffect, useMemo, useState } from 'react'
import { useApi } from '../api/client'
import type { Environment } from '../env/types'

export interface TokenRow {
  id: string
  friendly_name: string
  phone_number: string
  environment: Environment
  role: string | null
  sub: string | null
  created_at: string
  expires_at: string
  expired: boolean
}

const LS_KEY = 'assetsales-toolbox.activeToken'

// Shared across modules: lists the user's minted tokens and tracks the active
// one (persisted). The active token carries its own environment, so modules act
// against the env the token was minted for.
export function useTokens() {
  const api = useApi()
  const [tokens, setTokens] = useState<TokenRow[]>([])
  const [activeId, setActiveIdState] = useState<string | null>(() => localStorage.getItem(LS_KEY))

  const setActiveId = useCallback((id: string | null) => {
    setActiveIdState(id)
    if (id) localStorage.setItem(LS_KEY, id)
    else localStorage.removeItem(LS_KEY)
  }, [])

  const refresh = useCallback(async () => {
    const rows = await api.get<TokenRow[]>('/tokens')
    setTokens(rows)
    return rows
  }, [api])

  useEffect(() => { void refresh() }, [refresh])

  // Keep a sensible selection: drop a stale id, default to the newest live token.
  useEffect(() => {
    if (tokens.length === 0) return
    const stillValid = activeId && tokens.some((t) => t.id === activeId)
    if (!stillValid) {
      const firstLive = tokens.find((t) => !t.expired) ?? tokens[0]
      setActiveId(firstLive?.id ?? null)
    }
  }, [tokens, activeId, setActiveId])

  const activeToken = useMemo(() => tokens.find((t) => t.id === activeId) ?? null, [tokens, activeId])

  return { tokens, activeId, setActiveId, activeToken, refresh }
}
